import { datadogLogs } from '@datadog/browser-logs';
import { api } from './apiService';
import { Suggestion, Nudge, SuggestionType } from 'types/common';

// Parse comma-separated string into array of specific types
const parseCommaSeparated = <T extends string>(str: string): T[] => {
    if (!str || typeof str !== 'string' || str.trim() === '') {
        return [];
    }
    return str.split(',').map(item => item.trim()).filter(Boolean) as T[];
};
/**
 * Singleton NotificationService to handle polling for notifications
 */
class NotificationService {
    private static instance: NotificationService;
    private intervalId: number | undefined;
    private isPolling: boolean = false;
    private classId: string | number | null = null;
    private suggestionTypes: SuggestionType[] = [];
    private nudgeTypes: string[] = []; // Changed to string[] to handle all nudge types
    private pollingInterval: number = 10000;
    private listeners: Array<{
        onSuggestions: (suggestions: Suggestion[]) => void;
        onNudges: (nudges: Nudge[]) => void;
    }> = [];

    // Private constructor to prevent external instantiation
    private constructor() {
    }

    public static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    /**
     * Set up configuration
     */
    public configure(config: {
        suggestionTypesStr?: string;
        nudgeTypesStr?: string;
        pollingInterval?: string | number;
    }): this {
        if (config.suggestionTypesStr) {
            this.suggestionTypes = parseCommaSeparated<SuggestionType>(config.suggestionTypesStr);
        }

        if (config.nudgeTypesStr) {
            this.nudgeTypes = parseCommaSeparated<string>(config.nudgeTypesStr);
        }

        if (config.pollingInterval) {
            this.pollingInterval = typeof config.pollingInterval === 'number'
                ? config.pollingInterval
                : parseInt(String(config.pollingInterval), 10) || 10000;
        }

        return this;
    }

    /**
     * Start polling for notifications
     */
    public startPolling(classId: string | number): this {
        if (this.isPolling && this.classId === classId) {
            return this;
        }

        this.stopPolling();

        this.classId = classId;
        this.isPolling = true;

        // Initial fetch
        this.fetchNotifications();

        // Set up interval
        this.intervalId = window.setInterval(() => {
            this.fetchNotifications();
        }, this.pollingInterval);

        return this;
    }

    /**
     * Stop polling for notifications
     */
    public stopPolling(): this {
        if (this.intervalId) {
            window.clearInterval(this.intervalId);
            this.intervalId = undefined;
        }

        this.isPolling = false;
        this.classId = null;

        return this;
    }

    /**
     * Add a listener for notification updates
     */
    public addListener(listener: {
        onSuggestions: (suggestions: Suggestion[]) => void;
        onNudges: (nudges: Nudge[]) => void;
    }): this {
        this.listeners.push(listener);
        return this;
    }

    /**
     * Remove a listener
     */
    public removeListener(listener: {
        onSuggestions: (suggestions: Suggestion[]) => void;
        onNudges: (nudges: Nudge[]) => void;
    }): this {
        this.listeners = this.listeners.filter(l => l !== listener);

        // If no more listeners, stop polling
        if (this.listeners.length === 0) {
            this.stopPolling();
        }

        return this;
    }


    /**
     * Type guard to verify if an object is a valid Suggestion
     */
    private isSuggestion(obj: unknown): obj is Suggestion {
        return obj !== null
            && typeof obj === 'object'
            && 'id' in obj
            && 'message' in obj
            && 'suggestionType' in obj;
    }

    /**
     * Type guard to verify if an object is a valid Nudge
     */
    private isNudge(obj: unknown): obj is Nudge {
        return obj !== null
            && typeof obj === 'object'
            && 'id' in obj
            && 'message' in obj
            && 'nudgeType' in obj;
    }

    /**
     * Extract and validate array from API response
     */
    private extractSuggestions(response: unknown): Suggestion[] {
        if (!response) return [];

        let items: unknown[] = [];

        // Handle both array and { data: [...] } formats
        if (Array.isArray(response)) {
            items = response;
        } else if (response !== null && typeof response === 'object' && 'data' in response && Array.isArray((response as { data: unknown[] }).data)) {
            items = (response as { data: unknown[] }).data;
        } else {
            console.warn('Unexpected API response format for suggestions:', response);
            return [];
        }

        // Validate each item is a proper Suggestion
        const validSuggestions = items.filter(item => this.isSuggestion(item)) as Suggestion[];

        // For academic suggestions, only return those with visibleForTutor=true
        return validSuggestions.filter(suggestion => {
            // If it's an academic suggestion, check visibility flag
            if (suggestion.suggestionType === 'academic_suggestion') {
                return suggestion.visibleForTutor === true;
            }
            // For other suggestion types, include them all
            return true;
        });
    }

    /**
     * Extract and validate array from API response
     */
    private extractNudges(response: unknown): Nudge[] {
        if (!response) return [];

        let items: unknown[] = [];

        // Handle both array and { data: [...] } formats
        if (Array.isArray(response)) {
            items = response;
        } else if (response !== null && typeof response === 'object' && 'data' in response && Array.isArray((response as { data: unknown[] }).data)) {
            items = (response as { data: unknown[] }).data;
        } else {
            console.warn('Unexpected API response format for nudges:', response);
            return [];
        }

        // Validate each item is a proper Nudge
        return items.filter(item => this.isNudge(item)) as Nudge[];
    }



    /**
     * Fetch notifications and notify listeners
     */
    private async fetchNotifications(): Promise<void> {
        if (!this.classId) return;

        try {
            // Fetch suggestions
            const suggestionsResponse = await api.getSuggestions(this.classId);

            // Extract and validate suggestions array
            let suggestions = this.extractSuggestions(suggestionsResponse);

            // Filter by suggestion types if specified
            if (this.suggestionTypes.length > 0) {
                suggestions = suggestions.filter(
                    suggestion => this.suggestionTypes.includes(suggestion.suggestionType)
                );
            }

            // Sort by most recent, using visibleForTutorAt for academic suggestions
            suggestions.sort((a, b) => {
                // Use visibleForTutorAt for academic suggestions, fallback to createdAt
                const dateA = a.suggestionType === 'academic_suggestion' && a.visibleForTutorAt
                    ? new Date(a.visibleForTutorAt)
                    : (a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt));

                const dateB = b.suggestionType === 'academic_suggestion' && b.visibleForTutorAt
                    ? new Date(b.visibleForTutorAt)
                    : (b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt));

                return dateB.getTime() - dateA.getTime();
            });

            // Notify listeners
            this.listeners.forEach(listener => {
                try {
                    listener.onSuggestions(suggestions);
                } catch (error) {
                    datadogLogs.logger.error('Error notifying suggestion listener', {
                    }, error as Error);
                }
            });
        } catch (error) {
            datadogLogs.logger.error('Error fetching suggestions', {
                label: 'api-failure',
                api: 'fetchNotifications',
            }, error as Error);
        }

        try {
            // Fetch nudges
            const nudgesResponse = await api.getNudges(this.classId);

            // Extract and validate nudges array
            let nudges = this.extractNudges(nudgesResponse);
            // Filter by nudge types if specified
            if (this.nudgeTypes.length > 0) {
                nudges = nudges.filter(
                    nudge => this.nudgeTypes.includes(nudge.nudgeType)
                );
            }

            // Sort by most recent
            nudges.sort((a, b) => {
                const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
                const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
                return dateB.getTime() - dateA.getTime();
            });

            // Notify listeners
            this.listeners.forEach(listener => {
                try {
                    listener.onNudges(nudges);
                } catch (error) {
                    datadogLogs.logger.error('Error notifying nudge listener', {
                    }, error as Error);
                }
            });
        } catch (error) {
            datadogLogs.logger.error('Error fetching nudges', {
            }, error as Error);
        }
    }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance();